import { useEffect, useState, type FormEvent } from "react";
import { api } from "../../api/client";
import { useAppState } from "../../state/appState";
import { useToasts } from "../../state/toasts";
import { Empty } from "../../ui/components";

const MODES = [
  { id: "open", title: "Открыто", hint: "Заказы принимаются всегда" },
  { id: "scheduled", title: "По графику", hint: "Открытие и закрытие по расписанию ниже" },
  { id: "paused", title: "Временно не принимаем", hint: "Меню видно, оформить нельзя" },
  { id: "closed", title: "Закрыто", hint: "Точка не работает" }
] as const;

const WEEKDAYS = [
  { weekday: 1, title: "Понедельник" },
  { weekday: 2, title: "Вторник" },
  { weekday: 3, title: "Среда" },
  { weekday: 4, title: "Четверг" },
  { weekday: 5, title: "Пятница" },
  { weekday: 6, title: "Суббота" },
  { weekday: 0, title: "Воскресенье" }
];

function toTime(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

function toMinutes(value: string): number {
  const [hours, minutes] = value.split(":").map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}

export function SettingsPage() {
  const { snapshot } = useAppState();
  const { run } = useToasts();

  const settings = snapshot?.settings;
  const [form, setForm] = useState({
    shopMode: "open",
    shopMessage: "",
    minOrderAmount: "0",
    baseEtaMinutes: "15",
    autoStopByStock: false,
    costMethod: "average" as "average" | "fefo",
    legalName: "",
    legalDepartment: ""
  });
  const [ready, setReady] = useState(false);

  // Форма заполняется один раз: иначе живой поток событий затирал бы то,
  // что администратор печатает прямо сейчас.
  useEffect(() => {
    if (!settings || ready) return;
    setForm({
      shopMode: settings.shopMode,
      shopMessage: settings.shopMessage,
      minOrderAmount: String(settings.minOrderAmount),
      baseEtaMinutes: String(settings.baseEtaMinutes),
      autoStopByStock: settings.autoStopByStock,
      costMethod: settings.costMethod,
      legalName: settings.legalName,
      legalDepartment: settings.legalDepartment
    });
    setReady(true);
  }, [settings, ready]);

  if (!settings) return <Empty>Загрузка данных…</Empty>;

  async function save(event: FormEvent) {
    event.preventDefault();
    await run(
      () =>
        api.patch("/api/admin/settings", {
          shopMode: form.shopMode,
          shopMessage: form.shopMessage,
          minOrderAmount: Number(form.minOrderAmount) || 0,
          baseEtaMinutes: Number(form.baseEtaMinutes) || 1,
          autoStopByStock: form.autoStopByStock,
          costMethod: form.costMethod,
          legalName: form.legalName,
          legalDepartment: form.legalDepartment
        }),
      "Настройки сохранены"
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">Точка</p>
          <h1>Настройки</h1>
        </div>
      </div>

      <form className="panel" onSubmit={save}>
        <header>
          <div>
            <h2>Режим работы</h2>
            <p>В закрытом режиме меню можно смотреть, но оформить заказ нельзя.</p>
          </div>
        </header>

        <div className="field-row">
          {MODES.map(mode => (
            <label className="inline-check" key={mode.id}>
              <input
                type="radio"
                name="shopMode"
                checked={form.shopMode === mode.id}
                onChange={() => setForm(current => ({ ...current, shopMode: mode.id }))}
              />
              <span>
                {mode.title}
                <br />
                <small className="muted tiny">{mode.hint}</small>
              </span>
            </label>
          ))}
        </div>

        <label className="field">
          <span>Сообщение клиенту</span>
          <input
            value={form.shopMessage}
            onChange={event => setForm(current => ({ ...current, shopMessage: event.target.value }))}
            placeholder="Вернёмся через 20 минут"
          />
        </label>

        <div className="field-row">
          <label className="field">
            <span>Минимальная сумма заказа, ₽</span>
            <input
              inputMode="numeric"
              value={form.minOrderAmount}
              onChange={event => setForm(current => ({ ...current, minOrderAmount: event.target.value }))}
            />
          </label>
          <label className="field">
            <span>Базовое время приготовления, мин</span>
            <input
              inputMode="numeric"
              value={form.baseEtaMinutes}
              onChange={event => setForm(current => ({ ...current, baseEtaMinutes: event.target.value }))}
            />
          </label>
        </div>

        <div className="field-row">
          <label className="field">
            <span>Организация</span>
            <input
              value={form.legalName}
              onChange={event => setForm(current => ({ ...current, legalName: event.target.value }))}
              maxLength={200}
              placeholder="ООО «Парк Фуд»"
            />
            {/* В калькуляционной карте стоит юрлицо, а не вывеска: документ
                подписывают и хранят. */}
            <small className="tiny muted">Печатается в калькуляционной карте по форме ОП-1.</small>
          </label>

          <label className="field">
            <span>Структурное подразделение</span>
            <input
              value={form.legalDepartment}
              onChange={event => setForm(current => ({ ...current, legalDepartment: event.target.value }))}
              maxLength={200}
              placeholder="Точка в парке"
            />
          </label>
        </div>

        <label className="field">
          <span>Себестоимость списания</span>
          <select
            value={form.costMethod}
            onChange={event =>
              setForm(current => ({ ...current, costMethod: event.target.value as "average" | "fefo" }))
            }
          >
            <option value="average">Средневзвешенная</option>
            <option value="fefo">По партиям, первой уходит скоропортящаяся</option>
          </select>
          {/* Переключение меняет цифры, по которым уже посчитан прошлый
              фудкост, — об этом лучше сказать до, а не после. */}
          <small className="tiny muted">
            Партии и сроки годности ведутся в обоих режимах. Меняется только то, по какой цене
            уходит списание, — прошлые отчёты после переключения станут другими.
          </small>
        </label>

        <label className="inline-check">
          <input
            type="checkbox"
            checked={form.autoStopByStock}
            onChange={event => setForm(current => ({ ...current, autoStopByStock: event.target.checked }))}
          />
          Убирать блюдо из меню, когда сырья на порцию не хватает
        </label>

        <p className="tiny muted">
          Минимальная сумма проверяется по стоимости товаров до скидки. Прогноз времени = базовое время плюс надбавка за
          текущую загрузку кухни.
        </p>

        <p className="tiny muted">
          Автостоп по остаткам включайте, когда техкарты выверены, а приход заносится вовремя: иначе он снимет с продажи
          то, что на кухне есть.
        </p>

        <div className="modal-actions">
          <button className="btn btn-primary" type="submit">
            Сохранить
          </button>
        </div>
      </form>

      <SchedulePanel active={form.shopMode === "scheduled"} />
      <BoardPanel />
    </>
  );
}

/**
 * Ссылка для телевизора с номерами заказов.
 *
 * Экран открывает её один раз и живёт с ней месяцами, поэтому доступ по коду,
 * а не по учётной записи: сессия истекла бы в первую же ночь.
 */
function BoardPanel() {
  const { run, notify } = useToasts();
  const [board, setBoard] = useState<{ code: string; url: string } | null>(null);

  useEffect(() => {
    let cancelled = false;

    api
      .get<{ code: string; url: string }>("/api/admin/board")
      .then(data => {
        if (!cancelled) setBoard(data);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  async function reissue() {
    const confirmed = confirm(
      "Перевыпустить код экрана?\n\nТелевизор, открытый по старой ссылке, погаснет — на нём придётся открыть новую."
    );
    if (!confirmed) return;

    const ok = await run(async () => {
      setBoard(await api.once.post<{ code: string; url: string }>("/api/admin/board/reissue"));
    }, "Код экрана перевыпущен");

    if (!ok) return;
  }

  async function copy() {
    if (!board) return;
    try {
      await navigator.clipboard.writeText(board.url);
      notify("Ссылка скопирована", "ok");
    } catch {
      notify("Браузер не дал доступ к буферу обмена", "error");
    }
  }

  return (
    <section className="panel">
      <header>
        <div>
          <h2>Экран выдачи</h2>
          <p>
            Откройте эту ссылку на телевизоре. Экран показывает только номера заказов и их состояние — ни имён, ни
            сумм на нём нет.
          </p>
        </div>
        <div className="row-actions">
          <button type="button" className="btn btn-sm" onClick={() => void copy()} disabled={!board}>
            Скопировать
          </button>
          <a className="btn btn-sm" href={board?.url ?? "#"} target="_blank" rel="noreferrer">
            Открыть
          </a>
          <button type="button" className="btn btn-sm" onClick={() => void reissue()}>
            Перевыпустить код
          </button>
        </div>
      </header>

      <code className="tiny">{board?.url ?? "загрузка…"}</code>

      <p className="tiny muted">
        Перевыпуск гасит экраны, открытые по старой ссылке. Он нужен, если ссылка утекла наружу, — от повреждённого
        телевизора он не спасает.
      </p>
    </section>
  );
}

/**
 * Недельный график (п. 21 ТЗ).
 *
 * Сохраняется отдельно от настроек: расписание меняют редко и осознанно,
 * а режим работы иногда переключают несколько раз за смену.
 */
function SchedulePanel({ active }: { active: boolean }) {
  const { snapshot } = useAppState();
  const { run } = useToasts();
  const [days, setDays] = useState<{ weekday: number; closed: boolean; opensAt: number; closesAt: number }[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!snapshot?.schedule || ready) return;
    setDays(snapshot.schedule.map(day => ({ ...day })));
    setReady(true);
  }, [snapshot, ready]);

  function update(weekday: number, patch: Partial<{ closed: boolean; opensAt: number; closesAt: number }>) {
    setDays(current => current.map(day => (day.weekday === weekday ? { ...day, ...patch } : day)));
  }

  async function save() {
    await run(() => api.patch("/api/admin/schedule", { days }), "График сохранён");
  }

  if (!ready) return null;

  return (
    <section className="panel">
      <header>
        <div>
          <h2>График работы</h2>
          <p>
            {active
              ? "Точка открывается и закрывается по этому расписанию."
              : "Расписание сохранится, но заработает только в режиме «По графику»."}
          </p>
        </div>
      </header>

      <div className="schedule">
        {WEEKDAYS.map(({ weekday, title }) => {
          const day = days.find(item => item.weekday === weekday);
          if (!day) return null;

          return (
            <div className={`schedule-day ${day.closed ? "closed" : ""}`} key={weekday}>
              <strong>{title}</strong>

              <label className="inline-check">
                <input
                  type="checkbox"
                  checked={day.closed}
                  onChange={event => update(weekday, { closed: event.target.checked })}
                />
                выходной
              </label>

              <input
                type="time"
                value={toTime(day.opensAt)}
                disabled={day.closed}
                onChange={event => update(weekday, { opensAt: toMinutes(event.target.value) })}
              />
              <span className="muted">—</span>
              <input
                type="time"
                value={toTime(day.closesAt)}
                disabled={day.closed}
                onChange={event => update(weekday, { closesAt: toMinutes(event.target.value) })}
              />
            </div>
          );
        })}
      </div>

      <p className="tiny muted">
        Время закрытия раньше открытия означает смену через полночь — например, с 22:00 до 02:00. Ручные режимы
        «Закрыто» и «Временно не принимаем» всегда важнее графика.
      </p>

      <div className="modal-actions">
        <button type="button" className="btn btn-primary" onClick={() => void save()}>
          Сохранить график
        </button>
      </div>
    </section>
  );
}
